import { z } from "zod";

import { refreshResponseSchema } from "@/shared/api/browserAuthSchemas";

export { refreshResponseSchema };

const authUserWireSchema = z
  .object({
    display_name: z.string(),
    email: z.string(),
    id: z.string().min(1),
    phone_number: z.string(),
  })
  .passthrough();

export const authUserSchema = authUserWireSchema.transform((user) => ({
  displayName: user.display_name,
  email: user.email,
  id: user.id,
  phoneNumber: user.phone_number,
}));

export const authResponseSchema = z
  .object({
    access_token: z.string().min(1),
    access_token_expires_at: z.string().min(1),
    user: authUserWireSchema,
  })
  .catchall(z.unknown())
  .superRefine((response, context) => {
    if ("refresh_token" in response) {
      context.addIssue({
        code: "custom",
        message: "浏览器认证响应不得包含 refresh token",
      });
    }
  })
  .transform((response) => ({
    accessToken: response.access_token,
    accessTokenExpiresAt: response.access_token_expires_at,
    user: authUserSchema.parse(response.user),
  }));

export const invitationRegisterResponseSchema = z
  .object({
    access_token: z.string().min(1),
    access_token_expires_at: z.string().min(1),
    membership: z
      .object({
        id: z.string().min(1),
        is_owner: z.literal(false),
        organization_id: z.string().min(1),
        revision: z.number().int().positive(),
        role_id: z.string().min(1),
        status: z.literal("active"),
        user_id: z.string().min(1),
      })
      .strict(),
    organization: z
      .object({ id: z.string().min(1), name: z.string().min(1) })
      .strict(),
    user: authUserWireSchema,
  })
  .strict()
  .transform((response) => ({
    accessToken: response.access_token,
    accessTokenExpiresAt: response.access_token_expires_at,
    membership: response.membership,
    organization: response.organization,
    user: authUserSchema.parse(response.user),
  }));

const actorWireSchema = z
  .object({
    active_organization_id: z.string().min(1),
    organization_type: z.enum(["agency", "customer", "system"]),
    permissions: z.array(z.string()),
    role_id: z.string(),
    role_key: z.string(),
    scope: z.unknown(),
    user_id: z.string().min(1),
  })
  .strict();

export const meResponseSchema = z
  .object({ actor: actorWireSchema, user: authUserWireSchema })
  .strict()
  .transform((response) => ({
    actor: {
      activeOrganizationId: response.actor.active_organization_id,
      organizationType: response.actor.organization_type,
      userId: response.actor.user_id,
    },
    user: authUserSchema.parse(response.user),
  }));

export const sendCodeResponseSchema = z
  .object({
    expires_at: z.string().min(1),
    provider: z.string(),
    request_id: z.string(),
    status: z.literal("sent"),
  })
  .strict()
  .transform((response) => ({
    expiresAt: response.expires_at,
    requestId: response.request_id,
  }));

export const passwordResetResponseSchema = z
  .object({ status: z.literal("password_reset"), user: authUserWireSchema })
  .strict();

export const logoutResponseSchema = z
  .object({ status: z.literal("logged_out") })
  .strict();
